import { Controller } from "@hotwired/stimulus"
import { landPoints } from "../passport_map/land"
import { RAD } from "../passport_map/geo"
import { flatView, globeView, drawLand, routePoints, stroke, pointAt } from "../passport_map/world"

const MAX_IN_FLIGHT = 5
const TAIL = 0.3
const PALETTE = [ "#ef4444", "#f97316", "#eab308", "#22c55e", "#14b8a6", "#3b82f6", "#8b5cf6", "#ec4899", "#e11d48", "#0ea5e9" ]
const FONT = "ui-rounded, 'SF Pro Rounded', ui-sans-serif, system-ui, sans-serif"
const monthFormat = new Intl.DateTimeFormat(undefined, { month: "short", year: "numeric" })

// Passports stamped at more than one event fly their own routes, one after another at random
// moments, each leaving a dotted trail. Unlike the map there's no shared clock, so passports
// never move as a group. With globe set, the world is a globe that turns to follow the
// latest passport to take off.
export default class extends Controller {
  static targets = [ "stage", "land", "trails", "sky", "log", "playIcon", "pauseIcon" ]
  static values = { journeys: Object, bookPath: String, globe: Boolean }

  connect() {
    const { events, passports } = this.journeysValue
    this.events = events.map((event) => ({ ...event, flash: -Infinity }))
    this.travellers = passports.map((passport) => ({
      id: passport.id,
      stamps: passport.stamps.map(([ e, t ]) => ({ e, t })),
      color: PALETTE[Math.floor(Math.random() * PALETTE.length)]
    }))
    const stamps = passports.flatMap((passport) => passport.stamps)
    this.centerLongitude = stamps.reduce((sum, [ e ]) => sum + events[e].lng, 0) / Math.max(1, stamps.length)
    this.facing = { lng: this.centerLongitude, lat: 30 }
    this.landPoints = landPoints()
    this.paths = new Map()
    this.flights = []
    this.flown = []
    this.queue = []
    this.clock = 0
    this.nextDeparture = 0

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      this.travellers.forEach((passport) => passport.stamps.slice(1).forEach((_, i) => this.flown.push({ passport, i })))
      this.playing = false
    } else {
      this.playing = this.travellers.length > 0
    }
    this.showPlaying()
    this.resize()
    this.resizeObserver = new ResizeObserver(() => this.resize())
    this.resizeObserver.observe(this.stageTarget)
    this.tick = this.tick.bind(this)
    this.frame = requestAnimationFrame(this.tick)
  }

  disconnect() {
    cancelAnimationFrame(this.frame)
    this.resizeObserver.disconnect()
  }

  toggle() {
    this.playing = !this.playing && this.travellers.length > 0
    this.showPlaying()
  }

  showPlaying() {
    this.playIconTarget.classList.toggle("hidden", this.playing)
    this.pauseIconTarget.classList.toggle("hidden", !this.playing)
  }

  // Layout

  resize() {
    const dpr = window.devicePixelRatio || 1
    this.width = this.stageTarget.clientWidth
    this.height = this.stageTarget.clientHeight
    for (const canvas of [ this.landTarget, this.trailsTarget, this.skyTarget ]) {
      canvas.width = Math.round(this.width * dpr)
      canvas.height = Math.round(this.height * dpr)
      canvas.getContext("2d").setTransform(dpr, 0, 0, dpr, 0, 0)
    }
    this.layOut()
  }

  // Projects the world for the current size, and for a globe the way it faces. A globe turns
  // every frame, so it redraws land and trails each time; a flat map only on resize.
  layOut() {
    const padding = { top: this.width < 640 ? 120 : 150, right: 16, bottom: this.globeValue ? 90 : 16, left: 16 }
    this.view = this.globeValue
      ? globeView(this.width, this.height, { padding, ...this.facing })
      : flatView(this.width, this.height, { padding, centerLongitude: this.centerLongitude })
    this.paths.clear()
    this.events.forEach((event) => { event.xy = this.view.project([ event.lng, event.lat ]) })
    drawLand(this.landTarget.getContext("2d"), this.view, this.landPoints, this.width, this.height)
    this.drawTrails()
  }

  path(from, to) {
    const key = `${from}>${to}`
    if (!this.paths.has(key)) {
      const a = this.events[from]
      const b = this.events[to]
      this.paths.set(key, routePoints(this.view, [ a.lng, a.lat ], [ b.lng, b.lat ]))
    }
    return this.paths.get(key)
  }

  // Turns the globe a little towards the newest flight's position.
  turn(elapsed) {
    const flight = this.flights[this.flights.length - 1]
    if (!flight) return
    const a = this.events[flight.from]
    const b = this.events[flight.to]
    const target = { lng: (a.lng + b.lng) / 2 + (Math.abs(a.lng - b.lng) > 180 ? 180 : 0), lat: Math.max(-10, Math.min(45, (a.lat + b.lat) / 2)) }
    const k = 1 - Math.exp(-elapsed / 900)
    const dLng = ((target.lng - this.facing.lng + 540) % 360) - 180
    this.facing = { lng: this.facing.lng + dLng * k, lat: this.facing.lat + (target.lat - this.facing.lat) * k }
    this.layOut()
  }

  // Animation

  tick(now) {
    const elapsed = this.lastFrame ? Math.min(100, now - this.lastFrame) : 0
    this.lastFrame = now
    if (this.playing) {
      this.clock += elapsed
      this.depart()
      this.advance()
      if (this.globeValue) this.turn(elapsed)
    }
    this.drawSky()
    this.frame = requestAnimationFrame(this.tick)
  }

  depart() {
    if (this.clock < this.nextDeparture || this.flights.length >= MAX_IN_FLIGHT) return

    if (this.queue.length === 0) {
      this.queue = shuffle([ ...this.travellers ])
      // The biggest traveller opens the show; after a full round, the trails start over.
      if (this.flown.length === 0) {
        const star = this.queue.reduce((best, passport) => (passport.stamps.length > best.stamps.length ? passport : best))
        this.queue.splice(this.queue.indexOf(star), 1)
        this.queue.unshift(star)
      } else {
        this.flown = []
        this.drawTrails()
      }
    }
    const passport = this.queue.shift()
    this.flights.push(this.leg(passport, 0, this.clock))
    // Uneven gaps, sometimes two take off close together, sometimes a quiet moment.
    this.nextDeparture = this.clock + 400 + Math.random() ** 2 * 3200
  }

  leg(passport, i, at) {
    const from = passport.stamps[i].e
    const to = passport.stamps[i + 1].e
    const duration = 1100 + Math.sqrt(arc(this.events[from], this.events[to]) / Math.PI) * 3200 + Math.random() * 500
    return { passport, i, from, to, start: at, duration, hold: 350 + Math.random() * 700 }
  }

  advance() {
    this.flights = this.flights.flatMap((flight) => {
      const done = this.clock - flight.start - flight.duration
      if (done < 0) return [ flight ]

      if (!flight.landed) {
        flight.landed = true
        this.events[flight.to].flash = this.clock
        this.flown.push({ passport: flight.passport, i: flight.i })
        this.drawTrail(this.trailsTarget.getContext("2d"), this.flown[this.flown.length - 1])
        this.log(flight)
      }
      if (done < flight.hold) return [ flight ]
      const next = flight.i + 1
      return next < flight.passport.stamps.length - 1 ? [ this.leg(flight.passport, next, this.clock) ] : []
    })
  }

  log(flight) {
    const { passport, from, to, i } = flight
    const item = document.createElement("li")
    item.className = "flex items-center gap-2"
    const dot = document.createElement("span")
    dot.className = "size-2 shrink-0 rounded-full"
    dot.style.background = passport.color
    const link = document.createElement("a")
    link.className = "min-w-0 truncate hover:underline"
    link.href = this.bookPathValue.replace("ID", encodeURIComponent(passport.id.replace(/^Passport /, "")))
    link.textContent = `${passport.id}: ${this.events[from].city} → ${this.events[to].city}`
    const when = document.createElement("span")
    when.className = "ml-auto shrink-0 text-stone-400"
    when.textContent = monthFormat.format(new Date(passport.stamps[i + 1].t * 1000))
    item.append(dot, link, when)
    this.logTarget.prepend(item)
    while (this.logTarget.children.length > 5) this.logTarget.lastElementChild.remove()
  }

  // Drawing

  drawTrails() {
    const ctx = this.trailsTarget.getContext("2d")
    ctx.clearRect(0, 0, this.width, this.height)
    this.flown.forEach((trail) => this.drawTrail(ctx, trail))
  }

  drawTrail(ctx, { passport, i }) {
    const points = this.path(passport.stamps[i].e, passport.stamps[i + 1].e)
    ctx.save()
    ctx.strokeStyle = passport.color
    ctx.globalAlpha = 0.35
    ctx.lineWidth = 1.5
    ctx.lineCap = "round"
    ctx.setLineDash([ 0.5, 5 ])
    stroke(ctx, points, 0, 1)
    ctx.restore()
  }

  drawSky() {
    const ctx = this.skyTarget.getContext("2d")
    ctx.clearRect(0, 0, this.width, this.height)

    this.events.forEach((event) => {
      if (!event.xy) return
      const [ x, y ] = event.xy
      const age = (this.clock - event.flash) / 900
      if (age >= 0 && age < 1) {
        ctx.strokeStyle = `rgba(225, 29, 72, ${1 - age})`
        ctx.lineWidth = 2
        ctx.beginPath()
        ctx.arc(x, y, 4 + age * 18, 0, Math.PI * 2)
        ctx.stroke()
      }
      ctx.fillStyle = "#e11d48"
      ctx.beginPath()
      ctx.arc(x, y, 2.5, 0, Math.PI * 2)
      ctx.fill()
    })

    ctx.font = `600 12px ${FONT}`
    this.flights.forEach((flight) => {
      const points = this.path(flight.from, flight.to)
      const u = Math.min(1, (this.clock - flight.start) / flight.duration)
      const eased = u < 0.5 ? 2 * u * u : 1 - (-2 * u + 2) ** 2 / 2
      const color = flight.passport.color

      // The comet: a tail that fades out behind the head.
      ctx.lineCap = "round"
      const pieces = 12
      for (let k = 0; k < pieces; k++) {
        const a = Math.max(0, eased - TAIL + (TAIL * k) / pieces)
        const b = Math.max(0, eased - TAIL + (TAIL * (k + 1)) / pieces)
        if (b <= a) continue
        ctx.strokeStyle = color
        ctx.globalAlpha = ((k + 1) / pieces) * 0.9
        ctx.lineWidth = 1 + (2.5 * (k + 1)) / pieces
        stroke(ctx, points, a, b)
      }
      ctx.globalAlpha = 1

      const head = pointAt(points, eased)
      if (!head) return
      ctx.fillStyle = color
      ctx.shadowColor = color
      ctx.shadowBlur = 12
      ctx.beginPath()
      ctx.arc(head[0], head[1], 4, 0, Math.PI * 2)
      ctx.fill()
      ctx.shadowBlur = 0

      const text = flight.passport.id
      const width = ctx.measureText(text).width + 12
      const x = Math.min(this.width - width - 4, Math.max(4, head[0] + 8))
      const y = Math.max(4, head[1] - 24)
      ctx.fillStyle = "rgba(255, 255, 255, 0.92)"
      ctx.beginPath()
      ctx.roundRect(x, y, width, 18, 9)
      ctx.fill()
      ctx.fillStyle = "#44403c"
      ctx.fillText(text, x + 6, y + 13)
    })
  }
}

// Angle in radians between two events over the globe.
function arc(a, b) {
  const dLat = (b.lat - a.lat) * RAD
  const dLng = (b.lng - a.lng) * RAD
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * RAD) * Math.cos(b.lat * RAD) * Math.sin(dLng / 2) ** 2
  return 2 * Math.asin(Math.sqrt(h))
}

function shuffle(items) {
  for (let i = items.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [ items[i], items[j] ] = [ items[j], items[i] ]
  }
  return items
}
